import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ExternalUrl } from "@/common/interfaces";
import { TCustomQueryProps } from "@/common/type/hooks.types";
import { deleteService } from "@/modules/orders/services";
import {
  IService,
  IUpdateServiceAppointment,
  IUpdateServiceOperator,
  IUpdateServiceOrderedBy,
  IUpdateServiceStatus,
} from "@/modules/serviceOrdering/reducers";

import {
  getService,
  updateServiceAppointment,
  updateServiceOperator,
  updateServiceOrderedBy,
  updateServiceExternalUrl,
  updateStatus,
} from "../services";

export const useGetServiceById = (id: string) => {
  return useQuery({
    queryKey: ["getServiceById", id],
    queryFn: () => getService(id),
    enabled: !!id,
  });
};

export const useUpdateServiceOrderedBy = ({
  handleSuccess,
}: Partial<TCustomQueryProps<IUpdateServiceOrderedBy>> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IUpdateServiceOrderedBy) =>
      updateServiceOrderedBy(data?.serviceId, data?.userId),
    onSuccess: (_, variables) => {
      const { serviceId } = variables;

      queryClient.invalidateQueries({
        queryKey: ["getServiceById", serviceId],
      });

      handleSuccess?.(variables);
    },
    onError: (error: Error) => {
      console.error("Error when updating service created by:", error?.message);
    },
  });
};

export const useUpdateServiceOperator = ({
  handleSuccess,
}: Partial<TCustomQueryProps<IUpdateServiceOperator>> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IUpdateServiceOperator) =>
      updateServiceOperator(data?.serviceId, data?.operatorId),
    onSuccess: (_, variables) => {
      const { serviceId } = variables;

      queryClient.invalidateQueries({
        queryKey: ["getServiceById", serviceId],
      });

      handleSuccess?.(variables);
    },
    onError: (error: Error) => {
      console.error("Error when updating service operator:", error?.message);
    },
  });
};

export const useUpdateServiceStatus = ({
  handleSuccess,
}: Partial<TCustomQueryProps<IUpdateServiceStatus>> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IUpdateServiceStatus) =>
      updateStatus(data?.serviceId, data?.slug),
    onSuccess: (_, variables) => {
      const { serviceId } = variables;

      queryClient.invalidateQueries({
        queryKey: ["getServiceById", serviceId],
      });

      handleSuccess?.(variables);
    },
    onError: (error: Error) => {
      console.error("Error when updating service status:", error?.message);
    },
  });
};

export const useUpdateServiceAppointment = ({
  handleSuccess,
}: Partial<TCustomQueryProps<IUpdateServiceAppointment>> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IUpdateServiceAppointment) =>
      updateServiceAppointment(data?.serviceId, {
        startDate: data?.startDate,
        endDate: data?.endDate,
      }),
    onSuccess: (_, variables) => {
      const { serviceId } = variables;

      queryClient.invalidateQueries({
        queryKey: ["getServiceById", serviceId],
      });

      handleSuccess?.(variables);
    },
    onError: (error: Error) => {
      console.error("Error when updating service appointment:", error?.message);
    },
  });
};

export const useDeleteService = ({
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps<IService>> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteService(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getServiceById"] });

      handleSuccess?.();
    },
    onError: (error: Error) => {
      handleError?.();
      console.error("Error when deleting service:", error?.message);
    },
  });
};

export const useUpdateServiceExternalUrls = ({
  handleSuccess,
}: Partial<TCustomQueryProps<ExternalUrl[]>> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { serviceId: string; externalUrls: ExternalUrl[] }) =>
      updateServiceExternalUrl(data.serviceId, data.externalUrls),
    
    onMutate: async ({ serviceId, externalUrls }) => {
      await queryClient.cancelQueries({ queryKey: ["getServiceById", serviceId] });

      const previousService = queryClient.getQueryData(["getServiceById", serviceId]);

      queryClient.setQueryData(["getServiceById", serviceId], (old: IService | undefined) => {
        if (!old) return old;
        return {
          ...old,
          externalUrls: externalUrls,
        };
      });

      return { previousService, serviceId };
    },

    onError: (err, _variables, context) => {
      if (context?.previousService) {
        queryClient.setQueryData(["getServiceById", context.serviceId], context.previousService);
      }
      console.error("Error when updating service external URLs:", (err as Error)?.message);
    },

    onSettled: (_, __, { serviceId }) => {
      queryClient.invalidateQueries({ queryKey: ["getServiceById", serviceId] });
    },

    onSuccess: (_, variables) => {
      handleSuccess?.(variables.externalUrls);
    },
  });
};
